/**
 * EquityCurveChart.js — Equity Curve + Drawdown Band (Recharts)
 *
 * Props:
 *   series: [{label: string, color: string, data: number[] | {ts,equity,drawdown}[]}]
 *     - data가 number[]이면 정규화된 equity 값 (100 = 시작)
 *     - data가 {ts, equity, drawdown}[] 이면 API 응답 그대로
 *   benchmark: number[] | {ts, equity}[] | null — BTC 벤치마크 (선택)
 *   showDrawdown: boolean — 드로우다운 밴드 표시 여부 (기본 true)
 *   height: number — 차트 높이 (기본 260)
 *
 * Recharts 구현 규칙 (DESIGN_SYSTEM §5):
 *   - ResponsiveContainer 필수
 *   - 색상은 getThemeColors() 사용
 *   - Tooltip contentStyle: surfaceElevated 배경 + surfaceBorder 테두리
 *   - Grid stroke: surfaceElevated
 */
import React from 'react';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { getThemeColors } from '../../utils/themeColors';
import { isMobileViewport } from '../../utils/responsive';

/* ── 데이터 정규화 헬퍼 ── */

/**
 * series 항목의 data 배열을 { equity, drawdown, ts? } 형태로 통일
 * - number[] → equity=value, drawdown 계산
 * - {ts, equity, drawdown}[] → 그대로
 */
function normalizeSeries(rawData) {
  if (!rawData || rawData.length === 0) return [];
  if (typeof rawData[0] === 'number') {
    // raw number[] → peak 기반 drawdown 계산
    let peak = rawData[0];
    return rawData.map((v, i) => {
      peak = Math.max(peak, v);
      return {
        idx: i,
        equity: v,
        drawdown: peak > 0 ? ((v - peak) / peak) * 100 : 0,
      };
    });
  }
  // {ts, equity, drawdown}[] 형태
  return rawData.map((d, i) => ({
    idx: i,
    ts: d.ts,
    equity: d.equity,
    drawdown: d.drawdown ?? 0,
  }));
}

function normalizeBenchmark(rawBenchmark) {
  if (!rawBenchmark || rawBenchmark.length === 0) return null;
  if (typeof rawBenchmark[0] === 'number') return rawBenchmark;
  return rawBenchmark.map((d) => d.equity ?? d);
}

/**
 * series[] + benchmark → ComposedChart 용 단일 data 배열 빌드
 *
 * liveTip 이 있으면 마지막 실현 포인트 뒤에 "평가(mark-to-market)" 투영 포인트를
 * 1개 덧붙인다. 실현 곡선(s0)은 거기서 끊기고, 별도 `live` 키가 마지막 실현값에서
 * 평가값까지 점선 한 구간만 그린다. 청산 완료(실현)와 진입중(평가)의 경계를
 * 차트에서 직접 보이게 하는 장치 (DAS-147).
 */
function buildChartData(series, benchmark, liveTip) {
  const normalized = series.map((s) => normalizeSeries(s.data));
  const benchArr = normalizeBenchmark(benchmark);
  const len = normalized[0]?.length ?? 0;
  if (len === 0) return [];

  const data = Array.from({ length: len }, (_, i) => {
    const point = { idx: i };
    normalized.forEach((nd, si) => {
      point[`s${si}`] = nd[i]?.equity ?? null;
      // drawdown은 primary(s0) 에서만 가져옴
      if (si === 0) point.drawdown = nd[i]?.drawdown ?? 0;
      if (nd[i]?.ts) point.ts = nd[i].ts;
    });
    if (benchArr) {
      point.benchmark = benchArr[i] ?? null;
    }
    // drawdown band: peak 값 (shading 상단)
    let peak = normalized[0][0]?.equity ?? 100;
    normalized[0].slice(0, i + 1).forEach((d) => {
      peak = Math.max(peak, d.equity ?? 0);
    });
    point.peak = peak;
    return point;
  });

  // 평가(live) 투영 — 마지막 실현값에서 mark-to-market 값까지 점선 한 구간.
  if (liveTip && Number.isFinite(liveTip.equityIndex) && data.length > 0) {
    const lastRealized = data[data.length - 1].s0;
    if (lastRealized != null) {
      data[data.length - 1].live = lastRealized; // 점선 시작점(실현 끝)
      data.push({ idx: len, live: liveTip.equityIndex, liveTip: true });
    }
  }

  return data;
}

/* ── 커스텀 Tooltip ── */
function CustomTooltip({ active, payload, label, C, series }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      className="rounded-xl text-xs"
      style={{
        background: C.surfaceElevated,
        border: `1px solid ${C.surfaceBorder}`,
        padding: '8px 12px',
        minWidth: 140,
      }}
    >
      {payload[0]?.payload?.ts && (
        <div className="font-mono mb-1" style={{ color: C.contentMuted, fontSize: 12 }}>
          {payload[0].payload.ts}
        </div>
      )}
      {payload.map((entry, i) => {
        if (
          entry.dataKey === 'peak' ||
          entry.dataKey === 'benchmark_fill' ||
          entry.dataKey === 'live'
        )
          return null;
        const isDrawdown = entry.dataKey === 'drawdown';
        return (
          <div key={i} className="flex justify-between gap-3 font-mono" style={{ color: C.content }}>
            <span style={{ color: entry.stroke || entry.fill || C.contentSecondary }}>
              {isDrawdown ? 'MDD' : series[parseInt(entry.dataKey.replace('s', ''))]?.label ?? entry.name}
            </span>
            <span style={{ fontWeight: 600 }}>
              {isDrawdown
                ? `${(entry.value ?? 0).toFixed(2)}%`
                : (entry.value ?? 0).toFixed(2)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ── 평가(live) 투영 끝점의 펄스 도트 ── */
function LiveDot({ cx, cy, payload, color }) {
  if (cx == null || cy == null || !payload?.liveTip) return null;
  return (
    <g style={{ pointerEvents: 'none' }}>
      <circle cx={cx} cy={cy} r={5} fill={color} fillOpacity={0.3}>
        <animate
          attributeName="r"
          values="5;10;5"
          dur="1.7s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="fill-opacity"
          values="0.35;0.04;0.35"
          dur="1.7s"
          repeatCount="indefinite"
        />
      </circle>
      <circle cx={cx} cy={cy} r={3} fill={color} />
    </g>
  );
}

// 부호 포함 천단위 KRW — 평가손익 칩 표기용.
function fmtSignedKrw(v) {
  const n = Math.round(v ?? 0);
  const s = Math.abs(n).toLocaleString();
  return n > 0 ? `+${s}` : n < 0 ? `-${s}` : s;
}

/* ── 메인 컴포넌트 ── */
class EquityCurveChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      benchmarkVisible: true,
    };
  }

  toggleBenchmark = () => {
    this.setState((prev) => ({ benchmarkVisible: !prev.benchmarkVisible }));
  };

  render() {
    const {
      series = [],
      benchmark = null,
      showDrawdown = true,
      height = 260,
      liveTip = null,
    } = this.props;
    const { benchmarkVisible } = this.state;

    const C = getThemeColors();
    const isMobile = isMobileViewport();

    if (!series.length) return null;

    const hasLive = !!(liveTip && Number.isFinite(liveTip.equityIndex));
    const chartData = buildChartData(series, benchmark, hasLive ? liveTip : null);
    if (!chartData.length) return null;

    const yAxisWidth = isMobile ? 44 : 52;
    const hasDrawdown = showDrawdown;
    // 드로우다운 패널 높이 비율: 전체 height의 25%
    const ddHeight = hasDrawdown ? Math.round(height * 0.25) : 0;
    const equityHeight = height - ddHeight;

    // Y축 도메인: 자동 + 5% 여유
    const allEquity = chartData.flatMap((d) =>
      series.map((_, si) => d[`s${si}`]).filter((v) => v != null)
    );
    // 평가 투영 끝점이 도메인 밖으로 잘리지 않도록 포함.
    if (hasLive) allEquity.push(liveTip.equityIndex);
    const eMin = Math.min(...allEquity);
    const eMax = Math.max(...allEquity);
    const ePad = (eMax - eMin) * 0.05;
    const equityDomain = [
      Math.floor(eMin - ePad),
      Math.ceil(eMax + ePad),
    ];

    // series별 색상: props.color 또는 테마 fallback
    const seriesColors = [
      series[0]?.color || C.gain,
      series[1]?.color || C.brand,
      series[2]?.color || C.warning,
      series[3]?.color || C.loss,
    ];

    return (
      <div className="flex flex-col gap-0">
        {/* ── 범례 ── */}
        <div
          className="flex flex-wrap items-center gap-3 mb-2"
          style={{ fontSize: 12 }}
        >
          {series.map((s, i) => (
            <span
              key={i}
              className="flex items-center gap-1.5 font-mono"
              style={{ color: C.contentMuted }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: 10,
                  height: 2,
                  background: seriesColors[i],
                  verticalAlign: 'middle',
                }}
              />
              {s.label}
            </span>
          ))}
          {benchmark && (
            <button
              type="button"
              onClick={this.toggleBenchmark}
              className="flex items-center gap-1.5 font-mono cursor-pointer"
              style={{
                all: 'unset',
                cursor: 'pointer',
                color: benchmarkVisible ? C.contentMuted : C.contentMuted,
                opacity: benchmarkVisible ? 1 : 0.4,
                fontSize: 11,
              }}
              aria-label={benchmarkVisible ? 'BTC 벤치마크 숨기기' : 'BTC 벤치마크 표시'}
              aria-pressed={benchmarkVisible}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: 10,
                  borderTop: `1.4px dashed ${C.contentMuted}`,
                  verticalAlign: 'middle',
                }}
              />
              BTC 벤치
            </button>
          )}
          {hasDrawdown && (
            <span
              className="flex items-center gap-1.5 font-mono"
              style={{ color: C.contentMuted }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: 10,
                  height: 8,
                  background: C.loss,
                  opacity: 0.25,
                  verticalAlign: 'middle',
                }}
              />
              Drawdown
            </span>
          )}
          {hasLive && (
            <span
              className="flex items-center gap-1.5 font-mono"
              style={{ color: C.contentSecondary }}
              title="진입중 포지션의 미실현 평가손익을 반영한 현재 추정 equity"
            >
              <span
                style={{
                  display: 'inline-block',
                  width: 10,
                  borderTop: `1.6px dashed ${C.brand}`,
                  verticalAlign: 'middle',
                }}
              />
              LIVE 평가
              {liveTip.unrealizedKrw != null && (
                <span
                  style={{
                    color:
                      liveTip.unrealizedKrw > 0
                        ? C.gain
                        : liveTip.unrealizedKrw < 0
                          ? C.loss
                          : C.contentMuted,
                    fontWeight: 600,
                  }}
                >
                  {fmtSignedKrw(liveTip.unrealizedKrw)}
                </span>
              )}
              {liveTip.openCount > 0 && (
                <span style={{ color: C.contentMuted }}>· {liveTip.openCount}건</span>
              )}
            </span>
          )}
        </div>

        {/* ── 이퀴티 곡선 ── */}
        <ResponsiveContainer width="100%" height={equityHeight}>
          <ComposedChart
            data={chartData}
            margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={C.surfaceElevated}
              vertical={false}
            />
            <XAxis
              dataKey="idx"
              hide
            />
            <YAxis
              width={yAxisWidth}
              tick={{ fontSize: 10, fill: C.contentMuted, fontFamily: 'monospace' }}
              tickLine={false}
              axisLine={false}
              domain={equityDomain}
              tickFormatter={(v) => v.toFixed(0)}
            />
            <Tooltip
              content={(props) => (
                <CustomTooltip {...props} C={C} series={series} />
              )}
            />

            {/* ── 드로우다운 밴드 (Area 레이어 기법) ── */}
            {hasDrawdown && (
              <Area
                dataKey="peak"
                stroke="none"
                fill={C.loss}
                fillOpacity={0.12}
                legendType="none"
                isAnimationActive={false}
              />
            )}
            {hasDrawdown && (
              <Area
                dataKey="s0"
                stroke="none"
                fill={C.surfaceCard}
                fillOpacity={1}
                legendType="none"
                isAnimationActive={false}
              />
            )}

            {/* ── 벤치마크 (dashed) ── */}
            {benchmark && benchmarkVisible && (
              <Line
                dataKey="benchmark"
                stroke={C.contentMuted}
                strokeWidth={1.4}
                strokeDasharray="4 4"
                dot={false}
                legendType="none"
                isAnimationActive={false}
                name="BTC 벤치"
              />
            )}

            {/* ── equity 시리즈 lines ── */}
            {series.map((s, i) => (
              <Line
                key={i}
                dataKey={`s${i}`}
                stroke={seriesColors[i]}
                strokeWidth={i === 0 ? 2.2 : 1.6}
                dot={false}
                isAnimationActive={false}
                name={s.label}
                legendType="none"
              />
            ))}

            {/* ── 평가(live) 투영 — 실현 끝 → mark-to-market, 점선 + 펄스 도트 ── */}
            {hasLive && (
              <Line
                dataKey="live"
                stroke={C.brand}
                strokeWidth={1.8}
                strokeDasharray="4 3"
                connectNulls={false}
                dot={<LiveDot color={C.brand} />}
                activeDot={false}
                isAnimationActive={false}
                legendType="none"
                name="LIVE 평가"
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>

        {/* ── 드로우다운 % 패널 ── */}
        {hasDrawdown && (
          <ResponsiveContainer width="100%" height={ddHeight}>
            <ComposedChart
              data={chartData}
              margin={{ top: 0, right: 8, left: 0, bottom: 0 }}
            >
              <XAxis dataKey="idx" hide />
              <YAxis
                width={yAxisWidth}
                tick={{ fontSize: 10, fill: C.contentMuted, fontFamily: 'monospace' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => `${v.toFixed(0)}%`}
                domain={['auto', 0]}
              />
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={C.surfaceElevated}
                vertical={false}
              />
              <Area
                dataKey="drawdown"
                stroke={C.loss}
                strokeWidth={1}
                fill={C.loss}
                fillOpacity={0.2}
                dot={false}
                isAnimationActive={false}
                name="Drawdown %"
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    );
  }
}

export default EquityCurveChart;
